/**
 * Phase 3 Feature I — merge multiple DdxKnowledgeBundle projections (multi–chief complaint).
 */

import { stableBundleHash, applyTier2UnverifiedHardFloor } from './ddxKbProjection.js';
import { synthesizeNextBestActions } from './ddxNextBestActions.js';

/**
 * @param {import('./ddxTypes.js').DdxKnowledgeBundle[]} bundles - non-empty, same extraction implied
 * @param {import('./ddxChiefComplaintResolver.js').ChiefComplaintResolution} [ccResolution]
 * @returns {import('./ddxTypes.js').DdxKnowledgeBundle}
 */
export function mergeDdxKnowledgeBundles(bundles, ccResolution) {
  const valid = (bundles || []).filter((b) => b && typeof b === 'object');
  if (valid.length === 0) {
    return {
      chiefComplaintId: '',
      temporalBucket: 'indeterminate',
      kbVersion: '0',
      candidates: [],
      cantMiss: [],
      expectedButMissing: [],
      bundleHash: stableBundleHash('empty-merge'),
      nextBestActions: [],
      patientContext: undefined,
      activeContextWarnings: [],
      highImpactContextFlags: [],
    };
  }
  if (valid.length === 1) {
    const b = { ...valid[0] };
    if (ccResolution) b.ccResolution = ccResolution;
    return b;
  }

  const primaryCc = valid[0].chiefComplaintId || '';
  const temporalBucket = valid[0].temporalBucket || 'indeterminate';
  const kbVersion = valid.map((b) => b.kbVersion || '1').sort().join('+');

  /** @type {Map<string, import('./ddxTypes.js').ScoredDiagnosis>} */
  const candMap = new Map();
  for (const b of valid) {
    for (const c of b.candidates || []) {
      const prev = candMap.get(c.diagnosisId);
      if (!prev || (c.adjustedScore || 0) > (prev.adjustedScore || 0)) {
        candMap.set(c.diagnosisId, { ...c });
      } else if (prev && (c.matchedFeatures || []).length > (prev.matchedFeatures || []).length) {
        candMap.set(c.diagnosisId, { ...prev, matchedFeatures: mergeMatchedFeatures(prev.matchedFeatures, c.matchedFeatures) });
      }
    }
  }
  const candidates = applyTier2UnverifiedHardFloor([...candMap.values()]).sort(
    (a, b) => (b.adjustedScore || 0) - (a.adjustedScore || 0)
  );

  /** @type {Map<string, import('./ddxTypes.js').CantMissEntry>} */
  const cmMap = new Map();
  for (const b of valid) {
    for (const cm of b.cantMiss || []) {
      if (!cm.diagnosisId) continue;
      if (!cmMap.has(cm.diagnosisId)) cmMap.set(cm.diagnosisId, { ...cm });
    }
  }
  const cantMiss = [...cmMap.values()];

  /** @type {Map<string, import('./ddxTypes.js').ExpectedButMissing>} */
  const ebmMap = new Map();
  for (const b of valid) {
    for (const e of b.expectedButMissing || []) {
      const k = `${e.diagnosisId}|${e.featureId}`;
      if (!ebmMap.has(k)) ebmMap.set(k, { ...e });
    }
  }
  const expectedButMissing = [...ebmMap.values()];

  const hashPayload = JSON.stringify({
    merged: valid.map((b) => b.bundleHash || '').sort(),
    cc: valid.map((b) => b.chiefComplaintId).sort(),
  });
  const bundleHash = stableBundleHash(hashPayload);

  const patientContext = valid[0]?.patientContext;
  const activeContextWarnings = dedupeStrings(valid.flatMap((b) => b.activeContextWarnings || []));
  const highImpactContextFlags = dedupeStrings(valid.flatMap((b) => b.highImpactContextFlags || []));

  /** @type {import('./ddxTypes.js').DdxKnowledgeBundle} */
  let merged = {
    chiefComplaintId: primaryCc,
    chiefComplaintMulti: valid.map((b) => b.chiefComplaintId).filter(Boolean),
    temporalBucket,
    kbVersion,
    candidates,
    cantMiss,
    expectedButMissing,
    bundleHash,
    nextBestActions: [],
    gapFillMeta: valid.find((b) => b.gapFillMeta)?.gapFillMeta,
    tier2ProvisionalFromSources: dedupeTier2(valid.flatMap((b) => b.tier2ProvisionalFromSources || [])),
    sourceGroundingProvenance: dedupeStrings(valid.flatMap((b) => b.sourceGroundingProvenance || [])),
    patientContext,
    activeContextWarnings,
    highImpactContextFlags,
  };
  if (ccResolution) merged.ccResolution = ccResolution;

  merged.nextBestActions = synthesizeNextBestActions(merged);
  return merged;
}

/**
 * @param {import('./ddxTypes.js').MatchedEdge[] | undefined} a
 * @param {import('./ddxTypes.js').MatchedEdge[] | undefined} b
 */
function mergeMatchedFeatures(a, b) {
  const seen = new Set();
  const out = [];
  for (const e of [...(a || []), ...(b || [])]) {
    const k = `${e.featureId}|${e.edgeId}`;
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(e);
  }
  return out;
}

function dedupeTier2(rows) {
  const seen = new Set();
  const out = [];
  for (const r of rows) {
    const id = r?.diagnosisId || r?.label || JSON.stringify(r);
    if (seen.has(id)) continue;
    seen.add(id);
    out.push(r);
  }
  return out;
}

function dedupeStrings(arr) {
  return [...new Set(arr.map(String))];
}

/**
 * @param {string[]} chiefComplaintIds
 * @param {import('./ddxTypes.js').TemporalExtractionResult} extraction
 * @param {import('./ddxKbProjection.js').KbProjectionDeps} deps
 * @param {{ includeTier2?: boolean }} [options]
 * @param {import('./ddxChiefComplaintResolver.js').ChiefComplaintResolution} [ccResolution]
 * @returns {Promise<import('./ddxTypes.js').DdxKnowledgeBundle>}
 */
export async function projectKnowledgeBundleMultiCc(chiefComplaintIds, extraction, deps, options = {}, ccResolution) {
  const ids = [...new Set((chiefComplaintIds || []).filter(Boolean))];
  if (ids.length === 0) {
    return mergeDdxKnowledgeBundles([], ccResolution);
  }
  if (ids.length === 1) {
    const { projectKnowledgeBundle } = await import('./ddxKbProjection.js');
    const b = await projectKnowledgeBundle(ids[0], extraction, deps, options);
    if (ccResolution) b.ccResolution = ccResolution;
    return b;
  }
  const { projectKnowledgeBundle } = await import('./ddxKbProjection.js');
  const bundles = [];
  for (const id of ids) {
    bundles.push(await projectKnowledgeBundle(id, extraction, deps, options));
  }
  return mergeDdxKnowledgeBundles(bundles, ccResolution);
}
